/**
 * TheirWorldView — immersive, full-bleed takeover of a life capsule for
 * one specific person.
 *
 * Sections render from capsule.content_json:
 *   narrative         — prose passages
 *   image             — single image + credit
 *   image_gallery     — grid of images
 *   newspaper_quote   — stylised pull-quote with source line
 *   signals           — bullet list of atmospheric facts
 *   primary_source    — (future) blockquote from a verified document
 *
 * Between sections the view surfaces the person's own matching documents
 * and photos — the capsule is the stage, the person is the character on
 * it. Unmet enrichment prompts appear as subtle nudge cards at the end:
 * "tell us X and the capsule will fit tighter."
 */
import { useEffect, useState } from 'react'
import { api } from '../../services/api.js'

export default function TheirWorldView({ personId, slug, onClose }) {
  const [capsule, setCapsule]     = useState(null)
  const [person, setPerson]       = useState(null)
  const [docs, setDocs]           = useState([])
  const [delpher, setDelpher]     = useState(null)
  const [err, setErr]             = useState(null)

  useEffect(() => {
    // Lock body scroll while takeover is open.
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  useEffect(() => {
    if (!personId || !slug) return
    Promise.all([
      api.get(`/persons/${personId}/capsules/${slug}`),
      api.get(`/persons/${personId}`),
      api.get(`/persons/${personId}/documents`).catch(() => []),
    ]).then(([c, p, d]) => {
      setCapsule(c)
      setPerson(p)
      setDocs(d || [])
    }).catch(e => setErr(e.message))

    // Delpher enrichment is best-effort and slower; fire it in parallel so
    // the main capsule renders immediately and newspapers fill in after.
    api.get(`/persons/${personId}/capsules/${slug}/delpher`)
      .then(setDelpher)
      .catch(() => setDelpher({ place: null, items: [] }))
  }, [personId, slug])

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (err) return null
  if (!capsule || !person) {
    return (
      <div className="their-world-takeover">
        <div className="their-world-takeover__loading">Loading…</div>
      </div>
    )
  }

  const sections = capsule.content_json?.sections || []
  const fullName = [person.first_name, person.last_name].filter(Boolean).join(' ') || 'This person'

  return (
    <div className="their-world-takeover" onClick={onClose}>
      <div className="their-world-takeover__inner" onClick={e => e.stopPropagation()}>
        <button className="their-world-takeover__close" onClick={onClose} aria-label="Close">×</button>

        {/* Hero */}
        <div
          className="their-world-takeover__hero"
          style={{
            backgroundImage: capsule.hero_image_url ? `url(${capsule.hero_image_url})` : undefined,
          }}
        >
          <div className="their-world-takeover__hero-overlay" />
          <div className="their-world-takeover__hero-body">
            <div className="their-world-takeover__eyebrow">Their world</div>
            <h1 className="their-world-takeover__title">{capsule.title}</h1>
            {capsule.tagline && <div className="their-world-takeover__tagline">{capsule.tagline}</div>}
            <div className="their-world-takeover__meta">
              {fullName} · {capsule.start_year}
              {capsule.end_year !== capsule.start_year && `–${capsule.end_year}`}
            </div>
          </div>
          {capsule.hero_image_credit && (
            <div className="their-world-takeover__credit">{capsule.hero_image_credit}</div>
          )}
        </div>

        {/* Body — sections and the person's own dated documents interleaved
            chronologically. A doc with doc_date inside the capsule's window
            drops in between the narrative sections whose year brackets it,
            so Jan's letter from 1943 lands inside the occupation narrative,
            not as a footnote at the end. Undated docs gather in the coda. */}
        <div className="their-world-takeover__body">
          {interleave(sections, docs, capsule).map((item, i) =>
            item.kind === '__doc'
              ? <PersonalDoc key={`doc-${item.doc.id}`} doc={item.doc} />
              : <Section key={`sec-${i}`} section={item} />
          )}

          <PersonalInterleave person={person} docs={undatedDocs(docs, capsule)} />

          {capsule.unmet_prompts?.length > 0 && (
            <div className="their-world-nudges">
              <div className="their-world-nudges__header">Make this fit tighter</div>
              <div className="their-world-nudges__hint">
                We'd render {fullName.split(' ')[0]}'s version of this period more
                precisely with a few more facts. Answers update {fullName.split(' ')[0]}'s
                profile and this capsule refreshes.
              </div>
              {capsule.unmet_prompts.map((p, i) => (
                <NudgePrompt
                  key={i}
                  prompt={p}
                  personId={personId}
                  currentValue={person[p.field]}
                  onSaved={() => {
                    // Refetch capsule + person so unmet_prompts shrink and
                    // the personal coda shows the new fact.
                    api.get(`/persons/${personId}/capsules/${slug}`).then(setCapsule)
                    api.get(`/persons/${personId}`).then(setPerson)
                  }}
                />
              ))}
            </div>
          )}

          {delpher?.items?.length > 0 && (
            <div className="tw-delpher">
              <div className="tw-delpher__label">Newspapers of the time</div>
              <div className="tw-delpher__hint">
                Snippets from Delpher's digitised Dutch press archive, searched
                on <strong>{delpher.place}</strong> during this capsule's years.
                Click any item for the full article.
              </div>
              <div className="tw-delpher__list">
                {delpher.items.map(item => (
                  <a
                    key={item.id || item.url}
                    className="tw-delpher__item"
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div className="tw-delpher__item-head">
                      <span className="tw-delpher__item-pub">{item.publication || 'Newspaper'}</span>
                      {item.date && <span className="tw-delpher__item-date">{item.date}</span>}
                    </div>
                    {item.title && <div className="tw-delpher__item-title">{item.title}</div>}
                    {item.snippet && (
                      <div className="tw-delpher__item-snippet">“{item.snippet}…”</div>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}

          {capsule.source_urls?.length > 0 && (
            <div className="their-world-sources">
              <div className="their-world-sources__label">Sources</div>
              {capsule.source_urls.map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer" className="their-world-sources__link">
                  {prettyHost(u)} ↗
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Merge capsule sections with the person's dated documents into a single
// chronological list. A section keeps its own position if it has no year
// (stays where it was originally); dated docs are inserted in year order
// at the boundary between sections whose years bracket them.
function interleave(sections, docs, capsule) {
  const windowStart = capsule.start_year
  const windowEnd   = capsule.end_year

  const datedDocs = (docs || [])
    .map(d => ({ ...d, _year: parseDocYear(d) }))
    .filter(d => d._year && d._year >= windowStart && d._year <= windowEnd)
    .sort((a, b) => a._year - b._year)

  // Walk sections in order; before each section that has a year, flush
  // any pending docs whose year is <= that section's year.
  const out = []
  let d = 0
  for (const s of sections) {
    if (s.year != null) {
      while (d < datedDocs.length && datedDocs[d]._year <= s.year) {
        out.push({ kind: '__doc', doc: datedDocs[d++] })
      }
    }
    out.push(s)
  }
  // Any docs past the last year go after everything else.
  while (d < datedDocs.length) out.push({ kind: '__doc', doc: datedDocs[d++] })
  return out
}

function undatedDocs(docs, capsule) {
  return (docs || []).filter(d => {
    const y = parseDocYear(d)
    return !y || y < capsule.start_year || y > capsule.end_year
  })
}

function parseDocYear(doc) {
  const candidate = doc.doc_date || doc.extracted_fields?.document_date
  if (!candidate) return null
  const m = String(candidate).match(/\b(1[0-9]{3}|20[0-2][0-9])\b/)
  return m ? parseInt(m[1], 10) : null
}

function PersonalDoc({ doc }) {
  const year = parseDocYear(doc)
  const isImage = doc.mime_type?.startsWith('image/')
  const docTypeLabels = {
    birth_cert: 'Birth certificate', death_cert: 'Death certificate',
    marriage_cert: 'Marriage certificate', census: 'Census',
    military: 'Military record', photo: 'Photograph',
    newspaper: 'Newspaper', letter: 'Letter', other: 'Document',
  }
  return (
    <figure className="tw-section tw-section--personal-doc">
      <div className="tw-personal-doc__eyebrow">From the family records · {year || ''}</div>
      {isImage && doc.url && (
        <FallbackImage src={doc.url} alt={doc.file_name} />
      )}
      <figcaption>
        <strong>{docTypeLabels[doc.doc_type] || 'Document'}</strong>
        {doc.source_label && ` · ${doc.source_label}`}
        {doc.doc_date && ` · ${doc.doc_date}`}
        {doc.extracted_fields?.summary && (
          <div className="tw-personal-doc__summary">{doc.extracted_fields.summary}</div>
        )}
      </figcaption>
    </figure>
  )
}

function Section({ section }) {
  switch (section.kind) {
    case 'narrative':
      return (
        <div className="tw-section tw-section--narrative">
          {section.title && <h2 className="tw-section__title">{section.title}</h2>}
          <p className="tw-section__body">{section.body}</p>
        </div>
      )
    case 'image':
      return (
        <figure className="tw-section tw-section--image">
          <FallbackImage src={section.url} alt={section.caption || ''} />
          {section.caption && <figcaption>{section.caption}</figcaption>}
        </figure>
      )
    case 'image_gallery':
      return (
        <div className="tw-section tw-section--gallery">
          {(section.images || []).map((img, i) => (
            <figure key={i}>
              <FallbackImage src={img.url} alt={img.caption || ''} />
              {img.caption && <figcaption>{img.caption}</figcaption>}
            </figure>
          ))}
        </div>
      )
    case 'newspaper_quote':
      return (
        <blockquote className="tw-section tw-section--quote">
          <div className="tw-quote__body">“{section.text}”</div>
          {section.translation && (
            <div className="tw-quote__translation">{section.translation}</div>
          )}
          {section.source && <cite className="tw-quote__source">— {section.source}</cite>}
        </blockquote>
      )
    case 'signals':
      return (
        <div className="tw-section tw-section--signals">
          {section.title && <div className="tw-signals__title">{section.title}</div>}
          <ul>
            {(section.items || []).map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )
    case 'primary_source':
      return (
        <blockquote className="tw-section tw-section--primary">
          <div className="tw-primary__body">{section.text}</div>
          {section.source && <cite>{section.source}</cite>}
        </blockquote>
      )
    default:
      return null
  }
}

function PersonalInterleave({ person, docs }) {
  // Dated docs already interleaved inline with the narrative. This block
  // only surfaces the facts card + any undated docs as a coda — so the
  // reader lands on "who this was" after experiencing the world they were in.
  const hasAnyFacts = person.birth_date || person.death_date || person.birth_place || person.occupation
  if (!hasAnyFacts && (!docs || docs.length === 0)) return null

  return (
    <div className="tw-section tw-section--personal">
      <div className="tw-personal__eyebrow">In this world —</div>
      <div className="tw-personal__card">
        <div className="tw-personal__name">
          {[person.first_name, person.last_name].filter(Boolean).join(' ')}
        </div>
        <div className="tw-personal__facts">
          {person.birth_date  && <span>born {person.birth_date}</span>}
          {person.birth_place && <span>· {person.birth_place}</span>}
          {person.occupation  && <span>· {person.occupation}</span>}
          {person.death_date  && <span>· died {person.death_date}</span>}
        </div>
        {docs && docs.length > 0 && (
          <div className="tw-personal__docs">
            Plus {docs.length} undated document{docs.length === 1 ? '' : 's'} we couldn't place in time.
          </div>
        )}
      </div>
    </div>
  )
}

// Inline editor for a single enrichment prompt. Saves PATCH the person
// and (optionally) POST the tags_when_answered to person_tags. onSaved
// triggers the caller to refetch so the capsule re-ranks and the prompt
// disappears from unmet_prompts.
function NudgePrompt({ prompt, personId, currentValue, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(currentValue || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  async function save() {
    if (!value.trim()) return
    setBusy(true); setErr(null)
    try {
      await api.patch(`/persons/${personId}`, { [prompt.field]: value.trim() })
      // Best-effort tag flip. Tag-only failures don't roll back the field.
      if (prompt.tags_when_answered?.length) {
        for (const tag of prompt.tags_when_answered) {
          await api.post(`/persons/${personId}/tags`, { tag }).catch(() => {})
        }
      }
      setEditing(false)
      onSaved?.()
    } catch (e) {
      setErr(e.message || 'Failed to save')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="their-world-nudges__item">
      <div className="their-world-nudges__field">{fieldLabel(prompt.field)}</div>
      <div className="their-world-nudges__question">{prompt.question}</div>
      {editing ? (
        <div className="their-world-nudges__editor">
          {prompt.field === 'notes' ? (
            <textarea
              className="form-input"
              rows={2}
              value={value}
              onChange={e => setValue(e.target.value)}
              autoFocus
            />
          ) : (
            <input
              className="form-input"
              value={value}
              onChange={e => setValue(e.target.value)}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && save()}
            />
          )}
          <div className="their-world-nudges__editor-actions">
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={save}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancel</button>
          </div>
          {err && <div className="their-world-nudges__error">{err}</div>}
        </div>
      ) : (
        <button className="btn btn-ghost btn-sm their-world-nudges__answer" onClick={() => setEditing(true)}>
          {currentValue ? `Update ${fieldLabel(prompt.field).toLowerCase()}` : `Add ${fieldLabel(prompt.field).toLowerCase()} →`}
        </button>
      )}
    </div>
  )
}

// Hide a broken image entirely instead of the OS broken-icon — missing
// images should feel like thoughtful whitespace, not a bug.
function FallbackImage({ src, alt }) {
  const [failed, setFailed] = useState(false)
  if (!src || failed) return null
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  )
}

function fieldLabel(field) {
  const labels = {
    birth_place: 'Birth place',
    death_place: 'Death place',
    occupation:  'Occupation',
    notes:       'Notes',
    birth_date:  'Birth date',
    death_date:  'Death date',
  }
  return labels[field] || field
}

function prettyHost(u) {
  try { return new URL(u).host.replace(/^www\./, '') }
  catch { return u }
}
